import axios from "axios";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

function ShowdatesModal({
  isOpen,
  onClose,
  experienceId,
  slug,
  hostId,
  price,
  prices,
  isEditMode = false,
  onDateSelect,
  currencyId,
  currency_symbol,
  currency,
  packageId,
  guestType,
  slotDetails,
  maxLimits,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [guestCount, setGuestCount] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const datepickerRef = useRef(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const location = useLocation();

  const [slots, setSlots] = useState([]); // fetched slots here
  const navigate = useNavigate();
  const modalContentRef = useRef(null);

  // formatted placeholder like "September 2025"
  const todayPlaceholder = new Date().toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  // Helper to format time to 12hr am/pm
  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":");
    let h = parseInt(hours, 10);
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${minutes}${ampm}`;
  };

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const [showGuestEditModal, setShowGuestEditModal] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [guestData, setGuestData] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });

  const [participantCount, setParticipantCount] = useState({
    adults: location.state?.guests?.adults ?? 1,
    children: location.state?.guests?.children ?? 0,
    infants: location.state?.guests?.infants ?? 0,
  });

  // const handleCancelGuests = () => setShowGuestEditModal(false);
  const handleCountChange = (type, action) => {
    setParticipantCount((prev) => {
      const totalLimit = Number(maxLimits?.maxAdult) || Infinity; // combined total limit
      const totalAll = prev.adults + prev.children + prev.infants;

      let updated = { ...prev };

      switch (type) {
        case "adults":
          if (action === "increment" && totalAll < totalLimit) {
            updated.adults += 1;
          } else if (action === "decrement" && prev.adults > 1) {
            updated.adults -= 1;
          }
          break;

        case "children":
          if (action === "increment" && totalAll < totalLimit) {
            updated.children += 1;
          } else if (action === "decrement" && prev.children > 0) {
            updated.children -= 1;
          }
          break;

        case "infants":
          if (action === "increment" && totalAll < totalLimit) {
            updated.infants += 1;
          } else if (action === "decrement" && prev.infants > 0) {
            updated.infants -= 1;
          }
          break;

        //     case "infants":
        // if (action === "increment") {
        //   if (prev.infants >= 2) {
        //     toast.error("Infants cannot be more than 5");
        //   } else if (totalAll < totalLimit) {
        //     updated.infants += 1;
        //   }
        // } else if (action === "decrement" && prev.infants > 0) {
        //   updated.infants -= 1;
        // }
        // break;

        default:
          break;
      }

      return updated;
    });
  };

  const handleSaveGuests = (newData) => {
    setGuestData(newData);
    setShowBreakdown(true); // show breakdown after modal save
    setShowGuestEditModal(false); // close modal
  };

  const handleCancelGuests = () => {
    setShowGuestEditModal(false);
  };

  const fetchSlots = async (page = 1) => {
    if (loading) return;
    setLoading(true);
    try {
      const dateToFetch = selectedDate
        ? formatLocalDate(selectedDate)
        : formatLocalDate(new Date());
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/getSlotsByDate`,
        {
          params: {
            experience_id: experienceId,
            package_id: packageId,
            host_id: hostId,
            date: dateToFetch,
            currency_id: currencyId || 6,
            page: page, // ✅ pass page
            per_page: 6, // ✅ pass page limit if API expects it
          },
        }
      );

      if (res.data.status) {
        const newSlots = res.data.data.slots || [];

        // Append slots if not first page
        setSlots((prev) => (page === 1 ? newSlots : [...prev, ...newSlots]));

        // Update pagination
        setPagination({
          current_page: Number(res.data.data.pagination.current_page),
          last_page: Number(res.data.data.pagination.last_page),
        });
      }
    } catch (err) {
      console.error("Error fetching slots:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setPagination({ current_page: 1, last_page: 1 });
      setSlots([]);
      fetchSlots(1);
    }
  }, [isOpen, selectedDate]);

  // ✅ Dynamic total price calculator
  // const calculateTotal = () => {
  //   if (showGuestEditModal) {
  //     // Editing → use current edit values
  //     return (
  //       adults * (prices?.adult || 0) +
  //       children * (prices?.child || 0) +
  //       infants * (prices?.infant || 0)
  //     );
  //   }

  //   // After save → use saved guestData
  //   return (
  //     (guestData.adults || 0) * (prices?.adult || 0) +
  //     (guestData.children || 0) * (prices?.child || 0) +
  //     (guestData.infants || 0) * (prices?.infant || 0)
  //   );
  // };

  const calculateTotal = () => {
    if (showGuestEditModal) {
      // Editing → use current edit values
      return (
        participantCount.adults * (prices?.adult || 0) +
        participantCount.children * (prices?.child || 0) +
        participantCount.infants * (prices?.infant || 0)
      );
    }

    // After save → use saved guestData
    return (
      (guestData.adults || 0) * (prices?.adult || 0) +
      (guestData.children || 0) * (prices?.child || 0) +
      (guestData.infants || 0) * (prices?.infant || 0)
    );
  };

  const isSlotPast = (slot) => {
    const slotDateTime = new Date(`${slot.slot_date}T${slot.from_time}`);
    const now = new Date();
    return slotDateTime < now;
  };

  const loadmore = (currentPage) => {
    if (loading) return;
    const pageNum = Number(currentPage); // ✅ ensure number

    if (pageNum < pagination.last_page) {
      fetchSlots(pageNum + 1);
    }
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    const date = slot.slot_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(slot);
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <div
      id="show_details"
      className="modal fade all_modalss show"
      role="dialog"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content detailspgess" ref={modalContentRef}>
          <div className="modal-body">
            <div className="shedow_booox">
              <div className="al_titlss_ara">
                <h3>Select a time</h3>
                <button className="datecross" onClick={onClose}>
                  ✕
                </button>

                {/* Adult input UI */}
                {!isEditMode && (
                  <div className="input-row">
                    <div className="title">
                      <div className="title">
                        <h2 className="label">
                          {showBreakdown ? (
                            <span
                              className="guest-breakdown"
                              style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                color: "#333",
                              }}
                            >
                              {guestData.adults > 0 &&
                                `${guestData.adults} Adult${
                                  guestData.adults > 1 ? "s" : ""
                                }`}
                              {guestData.children > 0 &&
                                `, ${guestData.children} Child${
                                  guestData.children > 1 ? "ren" : ""
                                }`}
                              {guestData.infants > 0 &&
                                `, ${guestData.infants} Infant${
                                  guestData.infants > 1 ? "s" : ""
                                }`}
                            </span>
                          ) : (
                            `${guestCount} ${
                              guestType === "adult"
                                ? "Adult"
                                : guestType === "child"
                                ? "Child"
                                : "Infant"
                            }`
                          )}
                        </h2>
                      </div>

                      <p>
                        {calculateTotal().toFixed(2)} {currency || "AUD"} total
                      </p>
                    </div>

                    {showGuestEditModal && (
                      <div className="add_modalss all_modalss date_guestedit">
                        <div className="modal-container">
                          <h3>Edit Guests</h3>
                          <button
                            className="btn btn-secondary"
                            onClick={handleCancelGuests}
                          >
                            <img src="/images/cancel_c.png" alt="cancel" />
                          </button>

                          <div className="main_area_sect">
                            <div className="us_cntens">
                              <p>Adults</p>
                              <span>Age 13+</span>
                            </div>
                            <div className="biths_adses">
                              <span
                                className="addsss"
                                onClick={() =>
                                  handleCountChange("adults", "decrement")
                                }
                              >
                                -
                              </span>
                              <input
                                type="text"
                                value={participantCount.adults}
                                readOnly
                                className="form-control"
                              />
                              <span
                                className="addsss"
                                onClick={() =>
                                  handleCountChange("adults", "increment")
                                }
                              >
                                +
                              </span>
                            </div>
                          </div>

                          <div className="main_area_sect">
                            <div className="us_cntens">
                              <p>Children</p>
                              <span>Age 4–12</span>
                            </div>
                            <div className="biths_adses">
                              <span
                                className="addsss"
                                onClick={() =>
                                  handleCountChange("children", "decrement")
                                }
                              >
                                -
                              </span>
                              <input
                                type="text"
                                value={participantCount.children}
                                readOnly
                                className="form-control"
                              />
                              <span
                                className="addsss"
                                onClick={() =>
                                  handleCountChange("children", "increment")
                                }
                              >
                                +
                              </span>
                            </div>
                          </div>

                          <div className="main_area_sect">
                            <div className="us_cntens">
                              <p>Infants</p>
                              <span>Under 2</span>
                            </div>
                            <div className="biths_adses">
                              <span
                                className="addsss"
                                onClick={() =>
                                  handleCountChange("infants", "decrement")
                                }
                              >
                                -
                              </span>
                              <input
                                type="text"
                                value={participantCount.infants}
                                readOnly
                                className="form-control"
                              />
                              <span
                                className="addsss"
                                onClick={() =>
                                  handleCountChange("infants", "increment")
                                }
                              >
                                +
                              </span>
                            </div>
                          </div>

                          <div className="modal-actions">
                            <button
                              className="btn btn-primary full_wdth"
                              //         onClick={() =>
                              //   handleSaveGuests({
                              //     adults,
                              //     children,
                              //     infants,
                              //   })
                              // }
                              onClick={() =>
                                handleSaveGuests({
                                  adults: participantCount.adults,
                                  children: participantCount.children,
                                  infants: participantCount.infants,
                                })
                              }
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="input">
                      <a
                        onClick={() => setShowGuestEditModal(true)}
                        style={{
                          cursor: "pointer",
                          color: "#007bff",
                          fontWeight: "500",
                        }}
                      >
                        Edit guest
                      </a>
                    </div>

                    {/* {showBreakdown && (
                    <div className="input">
                      <button
                        className="adds_btn disabled:opacity-50"
                        disabled={guestCount <= 1}
                        onClick={() => setGuestCount((prev) => prev - 1)}
                      >
                        −
                      </button>
                      <span className="countss">{guestCount}</span>
                      <button
                        className="adds_btn"
                        onClick={() => setGuestCount((prev) => prev + 1)}
                      >
                        +
                      </button>
                    </div>
)} */}
                  </div>
                )}

                {/* Month filter */}
                <div
                  className="dates"
                  onClick={() => datepickerRef.current.setFocus()}
                >
                  <DatePicker
                    selected={selectedDate}
                    ref={datepickerRef}
                    onChange={(date) => setSelectedDate(date)}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={todayPlaceholder}
                    className="datepicker"
                    onChangeRaw={(e) => e.preventDefault()}
                    id="datepicker1"
                  />
                  <img
                    src="/images/calendar_icons.png"
                    className="clndders"
                    alt=""
                    onClick={() =>
                      document.getElementById("datepicker1").focus()
                    }
                  />
                </div>
              </div>

              {/* Slots list */}
              {loading && slots.length === 0 ? (
                <p className="text-center mt-3">Loading slots...</p>
              ) : slots.length > 0 ? (
                <>
                  {Object.entries(groupedSlots).map(([date, dateSlots]) => {
                    const formattedDate = new Date(date).toLocaleDateString(
                      "en-US",
                      {
                        day: "numeric",
                        month: "long",
                      }
                    );
                    const dayName = dateSlots[0]?.day_name;

                    return (
                      <div className="main_litsts" key={date}>
                        <h4>
                          {dayName}, {formattedDate}
                        </h4>

                        {dateSlots.map((slot, index) => (
                          <div
                            key={slot.id + slot.from_time}
                            onClick={() => {
                              if (slot.available_pax > 0 && !isSlotPast(slot)) {
                                setSelectedSlot(slot);
                                setSelectedIndex({ date, index });
                              }
                            }}
                            disabled={slot.available_pax === 0}
                            className={`litssst 
             ${
               selectedIndex?.date === date && selectedIndex?.index === index
                 ? "active"
                 : ""
             } 
            ${
              slot.available_pax === 0 || isSlotPast(slot)
                ? "opacity-50 cursor-not-allowed slot-expired"
                : ""
            }`}
                          >
                            <div className="lft_txt">
                              <h5>
                                {formatTime(slot.from_time)} -{" "}
                                {formatTime(slot.to_time)}
                              </h5>
                              <p>
                                {calculateTotal().toFixed(2)}{" "}
                                {currency || "AUD"} / Guest
                              </p>
                            </div>
                            <div className="rits_listst">
                              <p>
                                {slot.available_pax === 0
                                  ? "Sold Out"
                                  : `${slot.available_pax} spots left`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {/* 🔹 Load More Button */}
                  {pagination.current_page < pagination.last_page &&
                    !loading && (
                      <div className="text-center mt-3 mb-3">
                        <button
                          className="butn_al_areea"
                          onClick={() => loadmore(pagination.current_page)}
                        >
                          Load More
                        </button>
                      </div>
                    )}

                  {/* 🔹 Loading Spinner */}
                  {loading && (
                    <p className="text-center mt-2">Loading more slots...</p>
                  )}
                </>
              ) : (
                <p className="text-center mt-3">No slots available</p>
              )}
            </div>
          </div>

          {/* Bottom bar when a slot is selected */}
          {selectedSlot && (
            <div className="last_downpay">
              <div className="titlless">
                {/* <h3>
  {calculateTotal().toFixed(2)}
{" "}
  {currency || "AUD"}{" "}
  <span>
    for {adults} Adult{adults > 1 ? "s" : ""}, {children} Child
    {children !== 1 ? "ren" : ""}, {infants} Infant{infants !== 1 ? "s" : ""}
  </span>
</h3> */}

                <h3>
                  {calculateTotal().toFixed(2)} {currency || "AUD"}{" "}
                  {/* <span>
    for {guestData.adults} Adult{guestData.adults > 1 ? "s" : ""},{" "}
    {guestData.children} Child{guestData.children !== 1 ? "ren" : ""},{" "}
    {guestData.infants} Infant{guestData.infants !== 1 ? "s" : ""}
  </span> */}
                  <span>
                    {guestData.adults > 0 &&
                      `${guestData.adults} Adult${
                        guestData.adults > 1 ? "s" : ""
                      }`}
                    {guestData.children > 0 &&
                      `${guestData.adults > 0 ? ", " : ""}${
                        guestData.children
                      } Child${guestData.children > 1 ? "ren" : ""}`}
                    {guestData.infants > 0 &&
                      `${
                        guestData.adults > 0 || guestData.children > 0
                          ? ", "
                          : ""
                      }${guestData.infants} Infant${
                        guestData.infants > 1 ? "s" : ""
                      }`}
                  </span>
                </h3>
              </div>

              <div className="slt_ar_l">
                {isEditMode ? (
                  // ✅ Edit mode → show Save button
                  <button
                    onClick={() => {
                      const selectedSlotDate = selectedSlot?.slot_date || null;

                      if (onDateSelect) {
                        onDateSelect(selectedSlotDate, selectedSlot); // send slot_date as is + slot object
                      }
                      onClose();
                    }}
                    disabled={!selectedSlot}
                  >
                    Save
                  </button>
                ) : (
                  // ✅ Normal flow → show Next button
                  <button
                    disabled={!selectedSlot || isSlotPast(selectedSlot)}
                    onClick={() => {
                      const selectedSlotDate = selectedSlot.slot_date;
                      const token = localStorage.getItem("authToken");

                      // ✅ Parse user data safely
                      const user = JSON.parse(
                        localStorage.getItem("user") || "{}"
                      );

                      // 🧩 Check for host account
                      if (user?.user_type === 2) {
                        toast.error(
                          "Looks like you’re using a host account. Please switch to your Experience profile to book this activity."
                        );
                        return;
                      }

                      // if (user?.signup_complete_status === "0") {
                      //   toast.error("Please complete you profile");
                      //   setShowOtherInfoModal(true);
                      //   return;
                      // }

                      const totalGuestsCount =
                        (guestData.adults || 0) +
                        (guestData.children || 0) +
                        (guestData.infants || 0);

                      if (totalGuestsCount > selectedSlot.available_pax) {
                        toast.error(
                          `Please reduce guests. Only ${selectedSlot.available_pax} spot(s) available for this slot.`
                        );
                        return;
                      }

                      const guestDataObj = {
                        adults: guestData.adults,
                        children: guestData.children,
                        infants: guestData.infants,
                      };

                      const totalPrice =
                        (guestData.adults || 0) * (prices?.adult || 0) +
                        (guestData.children || 0) * (prices?.child || 0) +
                        (guestData.infants || 0) * (prices?.infant || 0);

                      const navState = {
                        slotDate: selectedSlotDate,
                        slotDetails: selectedSlot,
                        guests: guestDataObj,
                        totalPrice,
                        currencyId,
                        currency,
                        currency_symbol,
                        packageId,
                      };

                      // ✅ Navigate to correct page based on login
                      if (token) {
                        navigate(
                          `/checkout/${slug}?slot=${selectedSlot.id}`,
                          {
                            state: navState,
                          }
                        );
                      } else {
                        navigate(
                          `/confirm/${slug}?slot=${selectedSlot.id}`,
                          {
                            state: navState,
                          }
                        );
                      }
                    }}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay click close */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ position: "fixed", inset: 0 }}
      ></div>

      
    </div>
  );
}

export default ShowdatesModal;
